import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Daterange } from '@northfork/ng-basics'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { AccruedInterest } from '../models/accrued-interest.model'
import { CouponRateHistory } from '../models/coupon-rate-history.model'
import { Distribution, DistributionV2 } from '../models/distribution.model'
import { DividendHistory } from '../models/dividend-history.model'
import { FactorHistory } from '../models/factor-history.model'
import { MMFCharacteristics, MMFSettings } from '../models/mmf-settings.model'
import { SecurityData } from '../models/security-data.model'
import { getDateRangeAsHttpParams } from '../utils/daterange-utils'
import { DistributionsService } from './distributions.service'

export const DISTRIBUTIONS_URL = `helios-distributions`
export const SECURITY_DATA_URL = (securityId: number) =>
  `${DISTRIBUTIONS_URL}/securityData/${securityId}`

@Injectable({
  providedIn: 'root',
})
export class HttpDistributionsService extends DistributionsService {
  constructor(private readonly http: HttpClient) {
    super()
  }

  /** Get a @{link Distribution} for a security from the distributions service.
   *
   * @param securityId The id of the security to get the distribution for.
   * @param dateRange (optional) The date range to get the distribution for.
   */

  getDistribution(
    securityId: number,
    dateRange?: Daterange | null,
  ): Observable<Distribution[]> {
    const params = getDateRangeAsHttpParams(dateRange)
    return this.http
      .get<{ data: DistributionV2[] }>(`${DISTRIBUTIONS_URL}/${securityId}`, {
        params,
      })
      .pipe(map(x => this.flattenResponse(x.data)))
  }

  getFactorHistory(securityId: number): Observable<FactorHistory[]> {
    return this.http.get<FactorHistory[]>(
      `${DISTRIBUTIONS_URL}/${securityId}/factorHistory`,
    )
  }

  getCouponRateHistory(securityId: number): Observable<CouponRateHistory[]> {
    return this.http.get<CouponRateHistory[]>(
      `${DISTRIBUTIONS_URL}/${securityId}/couponRateHistory`,
    )
  }

  getDividendHistory(securityId: number): Observable<DividendHistory[]> {
    return this.http.get<DividendHistory[]>(
      `${DISTRIBUTIONS_URL}/${securityId}/dividendHistory`,
    )
  }

  getMMFSettings(
    securityId: number,
    accountId: number,
  ): Observable<MMFSettings> {
    return this.http.get<MMFSettings>(
      `${DISTRIBUTIONS_URL}/${securityId}/mmfCharacteristics?accountId=${accountId}`,
    )
  }

  updateMmfCharacteristics(
    mmfCharacteristics: MMFCharacteristics,
  ): Observable<string> {
    return this.http.put<string>(
      `${DISTRIBUTIONS_URL}/updateMmfCharacteristics`,
      mmfCharacteristics,
      { context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true) },
    )
  }

  getAccruedInterestByRate(
    securityId: number,
    dateRange?: Daterange | null,
  ): Observable<AccruedInterest[]> {
    const options = {
      params: getDateRangeAsHttpParams(dateRange),
    }
    return this.http.get<AccruedInterest[]>(
      `${DISTRIBUTIONS_URL}/${securityId}/accrualRates`,
      options,
    )
  }

  getAccruedInterestByAmount(
    securityId: number,
    dateRange?: Daterange | null,
  ): Observable<AccruedInterest[]> {
    const options = {
      params: getDateRangeAsHttpParams(dateRange),
    }
    return this.http.get<AccruedInterest[]>(
      `${DISTRIBUTIONS_URL}/${securityId}/accrualAmounts`,
      options,
    )
  }

  getSecurityData(securityId: number): Observable<SecurityData> {
    return this.http.get<SecurityData>(SECURITY_DATA_URL(securityId))
  }

  private flattenResponse(response: DistributionV2[] | null): Distribution[] {
    if (!response) return []
    return response.reduce(
      (mappedDistributions: Distribution[], distribution: DistributionV2) => {
        const newDistribution: Distribution = {
          tranId: distribution.tranId,
          legacyOrigId: distribution.legacyOrigId,
          acctId: distribution.acctId,
          tranTypeCode: distribution.tranTypeCode,
          calculatedEntryDate: distribution.calculatedEntryDate,
          actualEntryDate: distribution.actualEntryDate,
          actualSettleDate: distribution.actualSettleDate,
          calculatedPostDate: distribution.calculatedPostDate,
          actualPostDate: distribution.actualPostDate,
          clearedDate: distribution.clearedDate,
          actualAmount: distribution.actualAmount,
          actualAmountTotal: distribution.actualAmountTotal,
          rate: distribution.rate,
          loss: distribution.loss,
          originalUnits: distribution.originalUnits,
          factorizedUnits: distribution.factorizedUnits,
          factor: distribution.factor,
          secId: distribution.secId,
          assetId: distribution.assetId,
          currencyCode: distribution.currencyCode,
          breakStatus: distribution.breakStatus,

          // mappings
          matchedStatus: distribution.matchedStatus,
          entryDate: distribution.entryDate.date,
          settleDate: distribution.settleDate.date,
          postDate: distribution.postDate.date,
          entryDateDifference: distribution.entryDate.dateDiff,
          settleDateDifference: distribution.settleDate.dateDiff,
          postDateDifference: distribution.postDate.dateDiff,
          calculatedSettleDate: distribution.calculatedSettleDate,
          calculatedAmount: distribution.calculatedAmount,
          calculatedAmountTotal: distribution.calculatedAmountTotal,
          amountTotalDifference: distribution.amountTotalDifference,
          isCalculated: distribution.matchedStatus != 'ACTUAL_ONLY',
          inventoryType: distribution.inventoryType,
          collateralBroker: distribution.collateralBroker,
          actualPrepaidInterest: distribution.actualPrepaidInterest,
          calculatedPrepaidInterest: distribution.calculatedPrepaidInterest,
          paymentCurrencyCode: distribution.paymentCurrencyCode,
          isLlaRate: distribution.isLlaRate,
        }

        mappedDistributions.push(newDistribution)

        return mappedDistributions
      },
      [],
    )
  }
}
